import React from 'react';
import { SkeletonText, TooltipIcon } from 'carbon-components-react';
import { MapBoundary16, HelpFilled16 } from '@carbon/icons-react';

/**
 * Returns referrals that have at least one response with custom_properties.webMapURL,
 * collecting all matching response indices per referral.
 */
export function getReferralsWithWebMap(
  referrals: any[]
): Array<{
  referral: any;
  referralIndex: number;
  webMapResponses: Array<{ responseIndex: number }>;
}> {
  if (!Array.isArray(referrals)) return [];
  const result: Array<{
    referral: any;
    referralIndex: number;
    webMapResponses: Array<{ responseIndex: number }>;
  }> = [];
  referrals.forEach((referral: any, referralIndex: number) => {
    const responses = referral?.responses;
    if (!Array.isArray(responses)) return;
    const webMapResponses: Array<{ responseIndex: number }> = [];
    responses.forEach((r: any, idx: number) => {
      if (r?.custom_properties?.webMapURL) {
        webMapResponses.push({ responseIndex: idx });
      }
    });
    if (webMapResponses.length > 0) {
      result.push({ referral, referralIndex, webMapResponses });
    }
  });
  return result;
}

export const InteractiveMapList: React.FC<Props> = (props: Props) => {
  const {
    referralsWithWebMap,
    selectedReferral,
    selectedResponse,
    handleInteractiveMapSelection,
    selectionSource,
    responsesExpired,
    isLoading
  } = props;

  return (
    <div
      className={`get-plans-actions-interactive-map ${
        responsesExpired ? 'get-plans-actions-interactive-map-disabled' : ''
      }`}
    >
      <div className="get-plans-actions-interactive-map-header">
        <div className="get-plans-actions-interactive-map-header-title">
          <div className="get-plans-actions-interactive-map-header-icon-wrapper">
            <MapBoundary16 />
          </div>
          INTERACTIVE MAP
        </div>
        <div>
          <TooltipIcon
            tooltipText="Responses that include a web map. Select one to view the map."
            className="tooltip enquiry-details-tooltip"
          >
            <HelpFilled16 className="tooltip-icon" />
          </TooltipIcon>
        </div>
      </div>
      <div className="get-plans-actions-interactive-map-listing">
        {!isLoading &&
          referralsWithWebMap.map(
            ({ referral, referralIndex, webMapResponses }) => {
              if (webMapResponses.length === 1) {
                const { responseIndex } = webMapResponses[0];
                const isSelected =
                  selectionSource === 'interactive-map' &&
                  selectedReferral === referralIndex &&
                  (referral?.responses?.length < 2
                    ? selectedResponse === -1
                    : selectedResponse === responseIndex);
                return (
                  <div
                    className={`get-plans-actions-interactive-map-listing-row ${
                      referral.status === 'COMPLETED' && !responsesExpired
                        ? 'completed'
                        : ''
                    } ${responsesExpired ? 'expired' : ''} ${
                      isSelected ? 'selected' : ''
                    }`}
                    key={`${referralIndex}`}
                    onClick={
                      responsesExpired
                        ? undefined
                        : () =>
                            handleInteractiveMapSelection(
                              referralIndex,
                              responseIndex
                            )
                    }
                  >
                    <div
                      className={`get-plans-actions-interactive-map-listing-row-name ${
                        isSelected ? 'indicator-selected' : ''
                      }`}
                    >
                      {referral.name}
                    </div>
                  </div>
                );
              }

              return (
                <div key={`${referralIndex}`}>
                  <div
                    className={`get-plans-actions-interactive-map-listing-row ${
                      referral.status === 'COMPLETED' && !responsesExpired
                        ? 'completed'
                        : ''
                    } ${responsesExpired ? 'expired' : ''}`}
                  >
                    <div className="get-plans-actions-interactive-map-multiple-listing-row-name">
                      {referral.name}
                    </div>
                  </div>
                  {webMapResponses.map(({ responseIndex }) => {
                    const isResponseSelected =
                      selectionSource === 'interactive-map' &&
                      selectedReferral === referralIndex &&
                      selectedResponse === responseIndex;
                    return (
                      <div
                        key={responseIndex}
                        className="get-plans-actions-interactive-map-multiple-listing-subRow"
                      >
                        <div
                          className={`get-plans-actions-interactive-map-multiple-listing-subRow-indicator ${
                            isResponseSelected ? 'indicator-selected' : ''
                          }`}
                        ></div>
                        <div
                          className={`get-plans-actions-interactive-map-multiple-listing-row-response ${
                            responsesExpired ? 'expired' : 'completed'
                          } ${isResponseSelected ? 'selected' : ''}`}
                          onClick={
                            responsesExpired
                              ? undefined
                              : () =>
                                  handleInteractiveMapSelection(
                                    referralIndex,
                                    responseIndex
                                  )
                          }
                        >
                          Response {responseIndex + 1}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            }
          )}
        {isLoading && (
          <div className="get-plans-actions-interactive-map-listing-skeleton">
            {[1, 2].map((item: number) => (
              <div
                key={item}
                className="get-plans-actions-interactive-map-listing-skeleton-row"
              >
                <SkeletonText />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

type ReferralWithWebMap = {
  referral: any;
  referralIndex: number;
  webMapResponses: Array<{ responseIndex: number }>;
};

type Props = {
  referralsWithWebMap: ReferralWithWebMap[];
  selectedReferral: number;
  selectedResponse: number;
  handleInteractiveMapSelection: (
    referralIndex: number,
    responseIndex: number
  ) => void;
  selectionSource?: 'interactive-map' | 'responses';
  responsesExpired: boolean;
  isLoading?: boolean;
};

InteractiveMapList.displayName = 'InteractiveMapList';

export default React.memo(InteractiveMapList);
